import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import dayjs from 'dayjs'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { FunilEtapas } from '@/components/dashboard/FunilEtapas'
import { GraficoColunas } from '@/components/dashboard/GraficoColunas'
import { VolumePorEtapa } from '@/components/dashboard/VolumePorEtapa'
import { CardMetas } from '@/components/dashboard/CardMetas'
import { ArteCampanha } from '@/components/dashboard/ArteCampanha'
import { CardRankingProprio } from '@/components/dashboard/CardRankingProprio'
import { TituloSecao } from '@/components/dashboard/TituloSecao'
import { ChipMeuNivel } from '@/components/nivel/ChipMeuNivel'
import { permissoesNivelConsultor } from '@/utils/nivelConsultor'
import {
  FiltroPeriodo,
  ValorFiltroPeriodo,
  intervaloDoPeriodo,
  personalizadoValido,
  rotuloDoPeriodo,
} from '@/components/dashboard/FiltroPeriodo'
import { formatBRL, formatBRLCurto } from '@/components/dashboard/canais'
import { dashboardService } from '@/services/dashboardService'
import { resumoService } from '@/services/resumoService'
import { oportunidadeService } from '@/services/oportunidadeService'
import { reuniaoService } from '@/services/reuniaoService'
import { pendenciaService } from '@/services/pendenciaService'
import { CampanhaResumoDashboard, DashboardResumo, PeriodicidadeMeta } from '@/types/dashboard'
import { ComissoesResumoConsultor, PendenciasResumo, ReunioesResumo } from '@/types/resumos'
import { authStore } from '@/store/authStore'
import { tokens } from '@/theme'
import { formatDate } from '@/utils/formatters'

/** Pendência que ainda espera ação do consultor (mesmo recorte do card "Em aberto"). */
const PENDENCIA_EM_ABERTO = ['ABERTA', 'VISUALIZADA', 'REABERTA']

/**
 * Reunião que ainda vai acontecer (R01 #2). O filtro `status` do backend é exato
 * (`ReuniaoController`), então uma chamada sem status é filtrada aqui pelo conjunto —
 * CONFIRMADA/REAGENDADA também são compromissos futuros do consultor.
 */
const REUNIAO_FUTURA = ['AGENDADA', 'CONFIRMADA', 'REAGENDADA']

/**
 * Uma linha de "Ações do Consultor" (VC-06; antes "Próximas ações"). Derivada das MESMAS fontes das telas de destino
 * (pendências da inbox, reuniões da Agenda, `proximaAcao` persistida na reserva) —
 * a ordem pendência > reunião futura > próxima ação é apresentação, não regra nova.
 */
interface ProximaAcao {
  id: string
  titulo: string
  origem: 'Pendência' | 'Reunião' | 'Reserva'
  referencia: string
  rota: string
}

const estadoDeErro = (e: unknown): 'erro' | 'semPermissao' =>
  (e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro'

/** Rampa do laranja para séries de 3–4 colunas (mesma família das etapas). */
const RAMPA = ['#F9B79A', tokens.brandPrimaryLight, tokens.brandPrimary, tokens.brandPrimaryDark]

/** Qual campanha ilustra o card de arte: a mesma ordem do espelho `meta` do backend. */
const ORDEM_CAMPANHA: PeriodicidadeMeta[] = ['PERIODO', 'MENSAL', 'ANUAL']

/**
 * Dashboard do consultor (Figma 5:2, Onda 2 / A04; conceito BI em 11/09): "Olá, <nome>" +
 * data, quatro KPIs (1º escuro, último laranja), "Meu funil" (quantidade) e "Volume por
 * etapa" (R$) lado a lado (VC-01), metas + campanha + ranking próprio (VC-02/VC-04), três
 * blocos de indicadores (comissões, reuniões, pendências) e "Ações do Consultor" com `Abrir →`.
 *
 * Fontes (regra do contrato — KPI cita a mesma fonte da tabela):
 *  - Oportunidades ativas / Pipeline total / Comissão prevista / funil / metas ←
 *    `GET /v1/dashboard/resumo` (total NUNCA ponderado — gate C1);
 *  - Ranking ← `GET /v1/dashboard/ranking-proprio` (só a posição do próprio consultor);
 *  - Pendências ← `GET /v1/pendencias/resumo` (mesma fonte da tela Pendências e Documentos);
 *  - Reuniões ← `GET /v1/reunioes/resumo` (mesma fonte da Agenda);
 *  - Comissões ← `GET /v1/comissoes/resumo-consultor` (mesma fonte do Financeiro).
 *
 * Filtro de período comum (L13, §0 da Especificação Definitiva): o MESMO período vai ao
 * `/v1/dashboard/resumo?inicio&fim` e recorta "Oportunidades ativas", "Pipeline total",
 * "Meu funil" e "Volume por etapa" — todos de uma resposta só, então reconciliam entre si e
 * com o Pipeline (mesma regra de etapa; "Toda a carteira" = o Pipeline inteiro). Metas seguem
 * a janela da campanha; comissões, reuniões e pendências, as telas de origem. A tela só
 * traduz a opção em datas de calendário — nenhum número é calculado aqui. O ganho sem data
 * de assinatura vem marcado pelo backend como estimado (L14, Q33).
 */
export const DashboardConsultor: FC = () => {
  const navigate = useNavigate()
  const usuario = authStore((st) => st.usuario)

  const [resumo, setResumo] = useState<DashboardResumo | null>(null)
  // L13: período comum. "Toda a carteira" por padrão — o mesmo universo do Pipeline.
  const [periodo, setPeriodo] = useState<ValorFiltroPeriodo>({ opcao: 'TUDO' })
  /**
   * Intervalo enviado ao backend. Personalizado incompleto/invertido NÃO consulta (mantém o
   * último resultado) — nunca cai em "toda a carteira" por engano.
   */
  const [intervalo, setIntervalo] = useState<{ inicio: string; fim: string } | null>(null)
  const [periodoAplicado, setPeriodoAplicado] = useState<ValorFiltroPeriodo>({ opcao: 'TUDO' })
  /** Só a resposta da ÚLTIMA consulta vale (troca rápida de período). */
  const ultimaConsulta = useRef(0)
  const [pendencias, setPendencias] = useState<PendenciasResumo | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [estadoErro, setEstadoErro] = useState<'erro' | 'semPermissao'>('erro')

  // Indicadores complementares: cada um falha sozinho, sem derrubar a página.
  const [reunioes, setReunioes] = useState<ReunioesResumo | null>(null)
  const [comissoes, setComissoes] = useState<ComissoesResumoConsultor | null>(null)
  const [carregandoIndicadores, setCarregandoIndicadores] = useState(true)

  const [acoes, setAcoes] = useState<ProximaAcao[]>([])
  const [estadoAcoes, setEstadoAcoes] = useState<
    'carregando' | 'pronto' | 'erro' | 'semPermissao'
  >('carregando')

  const carregarResumos = useCallback(async () => {
    const consulta = ++ultimaConsulta.current
    setCarregando(true)
    setErro(null)
    try {
      const [r, p] = await Promise.all([
        // Consultor não recorta por canal — o backend ignoraria o parâmetro (6.7).
        intervalo ? dashboardService.getResumo(undefined, intervalo) : dashboardService.getResumo(),
        resumoService.pendencias(),
      ])
      if (consulta !== ultimaConsulta.current) return
      setResumo(r)
      setPendencias(p)
    } catch (e) {
      if (consulta !== ultimaConsulta.current) return
      setErro(e instanceof Error ? e.message : 'Falha ao carregar o resumo')
      setEstadoErro(estadoDeErro(e))
    } finally {
      if (consulta === ultimaConsulta.current) setCarregando(false)
    }
  }, [intervalo])

  const mudarPeriodo = (novo: ValorFiltroPeriodo) => {
    setPeriodo(novo)
    if (novo.opcao === 'PERSONALIZADO' && !personalizadoValido(novo)) return
    setPeriodoAplicado(novo)
    const proximo = intervaloDoPeriodo(novo)
    setIntervalo((atual) =>
      atual?.inicio === proximo?.inicio && atual?.fim === proximo?.fim ? atual : proximo,
    )
  }

  const carregarIndicadores = useCallback(async () => {
    setCarregandoIndicadores(true)
    const [r, c] = await Promise.allSettled([
      resumoService.reunioes(),
      resumoService.comissoesConsultor(),
    ])
    setReunioes(r.status === 'fulfilled' ? r.value : null)
    setComissoes(c.status === 'fulfilled' ? c.value : null)
    setCarregandoIndicadores(false)
  }, [])

  const carregarAcoes = useCallback(async () => {
    setEstadoAcoes('carregando')
    try {
      const [minhasPendencias, reunioesPage, oportunidadesPage] = await Promise.all([
        pendenciaService.minhas(),
        reuniaoService.listar({ page: 0, size: 50 }),
        oportunidadeService.listar(0, 100),
      ])
      const agora = Date.now()
      const daPendencia: ProximaAcao[] = minhasPendencias
        .filter((p) => PENDENCIA_EM_ABERTO.includes(p.status))
        .map((p) => ({
          id: `pend-${p.id}`,
          titulo: `Responder: ${p.titulo}`,
          origem: 'Pendência' as const,
          referencia: p.prazoResposta ? `Prazo ${formatDate(p.prazoResposta)}` : '—',
          rota: `/reservas/${p.oportunidadeId}/pendencias`,
        }))
      const daReuniao: ProximaAcao[] = (reunioesPage.content ?? [])
        .filter((r) => REUNIAO_FUTURA.includes(r.status) && new Date(r.dataInicio).getTime() >= agora)
        .sort((a, b) => new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime())
        .map((r) => ({
          id: `reu-${r.id}`,
          titulo: r.titulo,
          origem: 'Reunião' as const,
          referencia: dayjs(r.dataInicio).format('DD/MM/YYYY HH:mm'),
          rota: '/agenda',
        }))
      const daReserva: ProximaAcao[] = (oportunidadesPage.content ?? [])
        .filter((o) => !!o.proximaAcao)
        .map((o) => ({
          id: `res-${o.id}`,
          titulo: o.proximaAcao as string,
          origem: 'Reserva' as const,
          referencia: o.razaoSocial ?? '—',
          rota: '/reservas',
        }))
      setAcoes([...daPendencia, ...daReuniao, ...daReserva].slice(0, 3))
      setEstadoAcoes('pronto')
    } catch (e) {
      setEstadoAcoes(estadoDeErro(e))
    }
  }, [])

  useEffect(() => {
    void carregarResumos()
  }, [carregarResumos])

  useEffect(() => {
    void carregarIndicadores()
    void carregarAcoes()
  }, [carregarIndicadores, carregarAcoes])

  // Rótulo do período EFETIVAMENTE aplicado (um personalizado sendo digitado não muda a legenda).
  const filtrado = intervalo != null
  const rotuloPeriodo = useMemo(() => rotuloDoPeriodo(periodoAplicado), [periodoAplicado])
  const criterioPeriodo = filtrado
    ? 'Ganho entra pela data de assinatura; as demais etapas, pela data de criação da reserva — mesma regra de etapa do Pipeline.'
    : 'Toda a carteira: os mesmos números do Pipeline, etapa a etapa.'
  // L14 (Q33): parte do volume ganho sem data de assinatura — valor pronto do backend.
  const avisoEstimado = resumo?.periodo?.possuiEstimado
    ? `Ganho inclui ${formatBRL(resumo.periodo.volumeEstimadoNoPeriodo)} estimado (sem data de assinatura).`
    : null

  // "Oportunidades ativas" e "Pipeline total" vêm PRONTOS do backend
  // (`pipeline.ativas` / `pipeline.pipelineTotal`, regra única: etapa fora de GANHO/PERDIDO —
  // Onda 6 / A11, lacuna (d) da Rodada 2). A tela não soma mais os buckets de
  // `porEtapaComercial`; sem o campo (backend anterior) mostra "—".
  const oportunidadesAtivas = resumo?.pipeline.ativas ?? null
  const pipelineTotal = resumo?.pipeline.pipelineTotal ?? null
  const comissaoPrevista = resumo?.financeiro.comissaoProjetada
  // Metas (VC-02): números prontos do backend; a tela só escolhe qual campanha ilustrar.
  // AN-7: prefere a campanha de VOLUME; só sem nenhuma de volume usa a de RECEITA.
  const campanhaVigente: CampanhaResumoDashboard | null =
    (['VOLUME', 'RECEITA'] as const)
      .flatMap((tipo) =>
        ORDEM_CAMPANHA.map(
          (p) =>
            resumo?.metas?.find(
              (m) => (m.tipo ?? 'VOLUME') === tipo && m.periodicidade === p && m.campanha,
            )?.campanha,
        ),
      )
      .find((c): c is CampanhaResumoDashboard => !!c) ?? null
  // VC-04: 403/404 do ranking esconde o card e a linha redistribui as colunas.
  const [rankingOculto, setRankingOculto] = useState(false)

  const primeiroNome = usuario?.nome?.split(' ')[0] ?? 'Consultor'
  // AN-10: só a persona CONSULTOR (não o ONBOARDING) consulta o próprio nível.
  const veMeuNivel = permissoesNivelConsultor(usuario?.permissoes).veMeuNivel
  const hoje = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const colunasAcoes: ColunaTabelaAcao<ProximaAcao>[] = [
    {
      titulo: 'Ação',
      render: (a) => (
        <Typography variant="body2" fontWeight={600}>
          {a.titulo}
        </Typography>
      ),
    },
    { titulo: 'Origem', render: (a) => a.origem },
    { titulo: 'Referência', render: (a) => a.referencia },
  ]

  /** Bloco de indicador: esqueleto enquanto carrega, "sem dados" se a fonte falhou. */
  const bloco = (pronto: boolean, conteudo: () => JSX.Element) =>
    carregandoIndicadores ? (
      <EstadoDeLista estado="carregando" minHeight={160} />
    ) : pronto ? (
      conteudo()
    ) : (
      <EstadoDeLista estado="vazio" mensagem="Sem dados no momento" minHeight={160} />
    )

  return (
    <Box>
      <PageHeader
        title={`Olá, ${primeiroNome}`}
        subtitle={hoje.charAt(0).toUpperCase() + hoje.slice(1)}
        acao={
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* AN-10: nível do próprio consultor, só informativo; sem nível ou com falha, nada. */}
            {veMeuNivel && <ChipMeuNivel />}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              // Onda 3 / A07: a criação é a página /reservas/nova (o modal antigo saiu na Onda 8 / A16).
              onClick={() => navigate('/reservas/nova')}
            >
              Nova oportunidade
            </Button>
          </Stack>
        }
      />

      {/* L13: filtro de período comum — vale para os KPIs de volume e os dois gráficos. */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <FiltroPeriodo valor={periodo} onChange={mudarPeriodo} />
          <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 560 }}>
            O período vale para Oportunidades ativas, Pipeline total, Meu funil e Volume por
            etapa. Metas seguem a janela da campanha; comissões, reuniões e pendências seguem as
            telas de origem.
          </Typography>
        </Stack>
      </Paper>

      {erro ? (
        <EstadoDeLista
          estado={estadoErro}
          {...(estadoErro === 'erro' && {
            mensagem: 'Não foi possível carregar o resumo do dashboard',
            detalhe: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregarResumos() },
          })}
        />
      ) : (
        <>
          {/* KPIs — 1º card escuro e o do dinheiro em laranja (convenção da página). */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              {/* fonte: GET /v1/dashboard/resumo (pipeline.ativas) */}
              <KpiCard
                label="Oportunidades ativas"
                valor={oportunidadesAtivas ?? '—'}
                legenda={filtrado ? 'Criadas no período' : 'No seu pipeline'}
                variante="escura"
                carregando={carregando}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {/* fonte: GET /v1/pendencias/resumo (emAberto) */}
              <KpiCard
                label="Pendências"
                valor={pendencias?.emAberto ?? '—'}
                legenda={
                  pendencias != null ? `${pendencias.bloqueantes} bloqueantes` : undefined
                }
                carregando={carregando}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {/* fonte: GET /v1/dashboard/resumo (pipeline.pipelineTotal);
                  total, nunca "ponderado" (gate C1 — a probabilidade digitada saiu na 6.3) */}
              <KpiCard
                label="Pipeline total"
                valor={pipelineTotal != null ? formatBRL(pipelineTotal) : '—'}
                legenda={filtrado ? 'Em andamento, criadas no período' : 'Valor em andamento'}
                carregando={carregando}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {/* fonte: GET /v1/dashboard/resumo (financeiro.comissaoProjetada) */}
              <KpiCard
                label="Comissão prevista"
                valor={comissaoPrevista != null ? formatBRL(comissaoPrevista) : '—'}
                legenda="Projetada pela Originei"
                variante="marca"
                carregando={carregando}
              />
            </Grid>
          </Grid>

          {/* Meu funil (quantidade) e Volume por etapa (R$) — seis etapas comerciais (6.4)
              em colunas verticais, lado a lado no desktop e empilhados no mobile (VC-01);
              mesmo agregado dos KPIs acima (Dashboard não tem número próprio). */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Meu funil"
                  descricao="Quantas oportunidades há em cada etapa. Clique para abrir o pipeline."
                />
                {carregando ? (
                  <EstadoDeLista estado="carregando" minHeight={200} />
                ) : (
                  // fonte: GET /v1/dashboard/resumo (pipeline.porEtapaComercial)
                  <FunilEtapas
                    porEtapa={resumo?.pipeline.porEtapaComercial}
                    aoClicar={() => navigate('/pipeline')}
                    altura={150}
                    legenda={{
                      unidade: 'quantidade de reservas',
                      periodo: rotuloPeriodo,
                      notas: [criterioPeriodo],
                    }}
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Volume por etapa"
                  descricao="Valor estimado das reservas em cada etapa."
                />
                {carregando ? (
                  <EstadoDeLista estado="carregando" minHeight={200} />
                ) : (
                  // fonte: GET /v1/dashboard/resumo (pipeline.valorPorEtapaComercial)
                  <VolumePorEtapa
                    valorPorEtapa={resumo?.pipeline.valorPorEtapaComercial}
                    aoClicar={() => navigate('/pipeline')}
                    altura={150}
                    legenda={{
                      unidade: 'R$ (valor estimado das reservas)',
                      periodo: rotuloPeriodo,
                      notas: avisoEstimado ? [criterioPeriodo, avisoEstimado] : [criterioPeriodo],
                    }}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Metas, campanha e ranking (VC-02/VC-04): tudo pronto do backend —
              `metas` do /v1/dashboard/resumo e /v1/dashboard/ranking-proprio. */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={rankingOculto ? 7 : 5}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                {/* fonte: GET /v1/dashboard/resumo (metas; sem `metas` → meta legado) */}
                <CardMetas
                  modo="consultor"
                  metas={resumo?.metas}
                  metaLegada={resumo?.meta}
                  carregando={carregando}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={rankingOculto ? 5 : 4}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <ArteCampanha campanha={campanhaVigente} carregando={carregando} />
              </Paper>
            </Grid>
            {!rankingOculto && (
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2.5, height: '100%' }}>
                  {/* fonte: GET /v1/dashboard/ranking-proprio */}
                  <CardRankingProprio onOcultar={() => setRankingOculto(true)} />
                </Paper>
              </Grid>
            )}
          </Grid>

          {/* Indicadores da operação: cada bloco cita a mesma fonte da tela de destino. */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={4}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Comissões"
                  descricao="Mesmos números do Financeiro."
                  acao={
                    <Button size="small" onClick={() => navigate('/financeiro')}>
                      Ver extrato
                    </Button>
                  }
                />
                {bloco(comissoes != null, () => (
                  <GraficoColunas
                    altura={120}
                    formatoValor={formatBRLCurto}
                    itens={[
                      { chave: 'aReceber', rotulo: 'A receber', valor: Number(comissoes?.aReceber ?? 0), cor: RAMPA[2] },
                      // VC-03: nota já enviada aguardando pagamento (mesmo rótulo do KPI do Financeiro).
                      { chave: 'aguardandoNf', rotulo: 'NF enviada', detalhe: 'aguardando pagamento', valor: Number(comissoes?.aguardandoNf ?? 0), cor: RAMPA[1] },
                      { chave: 'pagoNoAno', rotulo: 'Pago no ano', valor: Number(comissoes?.pagoNoAno ?? 0), cor: tokens.brandInk },
                    ]}
                  />
                ))}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Reuniões"
                  descricao="Mesmos números da Agenda."
                  acao={
                    <Button size="small" onClick={() => navigate('/agenda')}>
                      Ver agenda
                    </Button>
                  }
                />
                {bloco(reunioes != null, () => (
                  <GraficoColunas
                    altura={120}
                    itens={[
                      { chave: 'agendadas', rotulo: 'Agendadas', valor: reunioes?.agendadas ?? 0, cor: RAMPA[1] },
                      { chave: 'confirmadas', rotulo: 'Confirmadas', valor: reunioes?.confirmadas ?? 0, cor: RAMPA[2] },
                      { chave: 'realizadas', rotulo: 'Realizadas', valor: reunioes?.realizadas ?? 0, cor: tokens.brandInk },
                    ]}
                  />
                ))}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Pendências"
                  descricao="Mesmos números da Central de Pendências."
                  acao={
                    <Button size="small" onClick={() => navigate('/pendencias')}>
                      Ver pendências
                    </Button>
                  }
                />
                {carregando ? (
                  <EstadoDeLista estado="carregando" minHeight={160} />
                ) : (
                  <GraficoColunas
                    altura={120}
                    itens={[
                      { chave: 'emAberto', rotulo: 'Em aberto', valor: pendencias?.emAberto ?? 0, cor: RAMPA[2] },
                      { chave: 'bloqueantes', rotulo: 'Bloqueantes', valor: pendencias?.bloqueantes ?? 0, cor: RAMPA[3] },
                      { chave: 'emAnalise', rotulo: 'Em análise', valor: pendencias?.emAnalise ?? 0, cor: RAMPA[1] },
                      { chave: 'concluidas', rotulo: 'Concluídas', valor: pendencias?.concluidas ?? 0, cor: tokens.brandInk },
                    ]}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Ações do Consultor (VC-06) — pendências em aberto, reuniões futuras e a `proximaAcao`
              persistida nas reservas (Fase 6, item 18). Três linhas com "Abrir →". */}
          <Box>
            <Typography variant="h4" sx={{ mb: 1.5 }}>
              Ações do Consultor
            </Typography>
            {estadoAcoes === 'carregando' ? (
              <EstadoDeLista estado="carregando" mensagem="Carregando ações do consultor…" />
            ) : estadoAcoes === 'erro' || estadoAcoes === 'semPermissao' ? (
              <EstadoDeLista
                estado={estadoAcoes}
                {...(estadoAcoes === 'erro' && {
                  mensagem: 'Não foi possível carregar as ações do consultor',
                  acao: { label: 'Tentar novamente', onClick: () => void carregarAcoes() },
                })}
              />
            ) : acoes.length === 0 ? (
              <EstadoDeLista
                estado="vazio"
                mensagem="Nenhuma ação pendente"
                detalhe="Pendências, reuniões e próximos passos das suas reservas aparecem aqui."
              />
            ) : (
              <TabelaAcao
                aria-label="Ações do Consultor"
                colunas={colunasAcoes}
                linhas={acoes}
                chaveDaLinha={(a) => a.id}
                onAbrir={(a) => navigate(a.rota)}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

export default DashboardConsultor
